import { useEffect, useRef, useState } from 'react';
import { DocxIcon, Dropdown, DropdownChangeEvent, DownloadIcon, Toast } from '@repo/atomic-components';
import { format } from 'date-fns';
import { RefObject } from 'react';
import { useAzureFileUpload } from '@repo/file-uploader';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { OutlineIconButton } from '@/ui/components/OutlineIconButton';
import { fullWidthThemedDropdownPt } from '@/ui/components/themedDropdownPt';
import {
  ActiveBundlePageType,
  selectActiveBundlePageType,
  selectCaseIdData,
  selectSharedDataActiveTimelineChronologyType,
  TimelineChronologyType,
  useAppSelector,
} from '@/store';
import {
  TemplateType,
  useGetTemplateListQuery,
  useLazyGetChronologyGetDownloadQuery,
  useLazyGetTimelineGetDownloadQuery,
} from '@/api';
import { AccessControlAction } from '@/constants';
import { useAccessControl, useLocalStorageTemplateId } from '@/hooks';

const DEFAULT_TEMPLATE_LABEL = 'Default template';

// localStorage key for remembering the user's last picked chronology template.
const CHRONOLOGY_TEMPLATE_STORAGE_KEY = 'chronology_template_id';

interface ChronologyDownloadButtonProps {
  toastRef: RefObject<Toast | null>;
}

export const ChronologyDownloadButton = ({ toastRef }: ChronologyDownloadButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { downloadFile } = useAzureFileUpload();

  const caseId = useAppSelector(selectCaseIdData);
  const activeType = useAppSelector(selectSharedDataActiveTimelineChronologyType);
  const activeBundlePageType = useAppSelector(selectActiveBundlePageType);
  const isChronology = activeType === TimelineChronologyType.CHRONOLOGY;

  const [triggerTimelineGetDownload] = useLazyGetTimelineGetDownloadQuery();
  const [triggerChronologyGetDownload] = useLazyGetChronologyGetDownloadQuery();

  // Custom CHRONOLOGY templates for the current org — surfaced as extra
  // choices in the dropdown alongside the default template. Only fetched
  // when the user has permission to export, since the picker is gated
  // behind the same access-control action.
  const isExportBundlesTimelinesChronologies = useAccessControl(
    AccessControlAction.EXPORT_BUNDLES_TIMELINES_CHRONOLOGIES,
  );
  const { data: chronologyTemplatesData } = useGetTemplateListQuery(
    { templateType: TemplateType.CHRONOLOGY },
    { skip: !isExportBundlesTimelinesChronologies || !isChronology },
  );
  const chronologyTemplates = chronologyTemplatesData?.templates.filter((template) => template.uploadedAt) ?? [];

  const [selectedTemplateId, setSelectedTemplateId] = useLocalStorageTemplateId(
    CHRONOLOGY_TEMPLATE_STORAGE_KEY,
    chronologyTemplates,
  );

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const hasCustomTemplates = chronologyTemplates.length > 0;

  const handleDownload = () => {
    if (!caseId) return;

    if (activeType === TimelineChronologyType.TIMELINE) {
      triggerTimelineGetDownload({ caseId })
        .unwrap()
        .then((data) => {
          if (data?.downloadPath && data?.downloadToken) {
            downloadFile(
              `${caseId}_timeline_${format(new Date(), 'y_MMM_d_HH_mm')}`,
              data.downloadPath,
              data.downloadToken,
            );
          }
        })
        .catch(() => {
          toastRef.current?.show({
            severity: 'error',
            detail: 'Failed to download file. Please try again.',
            life: 5000,
          });
        });
      return;
    }

    triggerChronologyGetDownload({
      caseId,
      return_full_bundle_page_numbers: activeBundlePageType === ActiveBundlePageType.FULL,
      template_id: selectedTemplateId ?? undefined,
    })
      .unwrap()
      .then((data) => {
        if (data?.downloadPath && data?.downloadToken) {
          downloadFile(
            `${caseId}_chronology_${format(new Date(), 'y_MMM_d_HH_mm')}`,
            data.downloadPath,
            data.downloadToken,
          );
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: 'error',
          detail: 'Failed to download file. Please try again.',
          life: 5000,
        });
      });
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <OutlineIconButton
        icon={<DownloadIcon />}
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Download options"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      />

      {isOpen && (
        <div
          role="dialog"
          aria-label="Download options"
          className="border-fletchers-gray-110 bg-fletchers-white absolute top-[calc(100%+8px)] left-0 z-50 w-[400px] rounded-[8px] border shadow-lg"
        >
          {/* Header */}
          <div className="border-fletchers-gray-110 flex items-center border-b px-6 py-4">
            <div className="text-[14px] font-semibold text-black">Export</div>
          </div>

          {/* Chronology / Timeline section */}
          <div className="px-6 pt-4 pb-6">
            <div className="mb-1 flex items-center gap-2">
              <DocxIcon width={25} height={17} />
              <span className="text-[14px] font-semibold text-black">
                {isChronology ? 'Chronology table' : 'Timeline'}
              </span>
            </div>
            <p className="text-fletchers-gray-200 mb-3 text-[14px] leading-5">
              {isChronology ? 'Select a template and download the chronology' : 'Download the timeline as a document'}
            </p>

            {isChronology ? (
              <div className="flex items-center gap-2">
                <Dropdown
                  value={selectedTemplateId}
                  options={[
                    { label: DEFAULT_TEMPLATE_LABEL, value: null },
                    ...chronologyTemplates.map((template) => ({
                      label: template.templateName,
                      value: template.templateId,
                    })),
                  ]}
                  onChange={(event: DropdownChangeEvent) =>
                    setSelectedTemplateId((event.value as string | null) ?? null)
                  }
                  disabled={!hasCustomTemplates}
                  placeholder={DEFAULT_TEMPLATE_LABEL}
                  appendTo="self"
                  {...fullWidthThemedDropdownPt()}
                  className="flex-1"
                />
                <OutlineButton label="Download" icon={<DownloadIcon />} className="gap-2" onClick={handleDownload} />
              </div>
            ) : (
              <div className="flex justify-end">
                <OutlineButton label="Download" icon={<DownloadIcon />} className="gap-2" onClick={handleDownload} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
